import { Link } from "react-router-dom";
import { RefreshCw, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import {
  formatRequestedModulesLabel,
  formatRequiresInvoiceLabel,
  invoiceStatusLabel,
  licenseRequestStatusLabel,
  moduleLabel,
  requestChannelLabel,
} from "@/lib/commercialLicenseRequests";
import {
  cfdiUseSelectLabel,
  paymentMethodSelectLabel,
} from "@/lib/commercialInvoiceCatalog";
import { licenseTypeLabel } from "@/lib/licenseTypes";
import { billingCadenceLabel, commercialPlanLabel } from "@/lib/commercialPlanUi";
import {
  canExpandIssuedPlatformLicenseRequest,
  canFulfillPlatformLicenseRequest,
  isPaymentProofPending,
  PLATFORM_LICENSE_REQUEST_EMIT_PATH,
  PLATFORM_LICENSE_REQUEST_MSG_APPROVED_NEXT,
  PLATFORM_LICENSE_REQUEST_MSG_PAYMENT_PENDING,
  licenseRequestDisplayName,
  licenseRequestDisplayRfc,
  licenseRequestHasCommercialDrift,
  licenseRequestOriginalDisplayName,
  licenseRequestOriginalRfc,
  licenseRequestOriginalSummary,
  licenseRequestCurrentCommercialName,
  licenseRequestCurrentCommercialRfc,
  licenseRequestCurrentCommercialSourceLabel,
  licenseRequestCommercialCodeLabel,
} from "@/lib/platformLicenseRequestsUi";
import {
  formatExpansionLimitsSummary,
  formatScopeSummary,
  isExpansionRequestKind,
  paymentValidationStatusLabel,
  requestHistoryEventLabel,
  requestKindLabel,
  safeDisplayValue,
} from "@/lib/platformLicenseRequestDetailUi";
import { commercialToolsLinkQuery } from "@/lib/platformCommercialToolsUi";
import { platformAgreementAcceptancePdfUrl, platformPaymentProofDownloadUrl } from "@/lib/platformApi";

function DetailSection({ title, children, testId }) {
  return (
    <section
      className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2.5 space-y-1.5"
      data-testid={testId}
    >
      <h4 className="text-xs font-semibold text-[#0F2D5C] m-0">{title}</h4>
      {children}
    </section>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div>
      <dt className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</dt>
      <dd className={`text-xs text-slate-900 mt-0.5 m-0 break-words ${mono ? "font-mono" : ""}`}>
        {value}
      </dd>
    </div>
  );
}

function RequestedModuleBadges({ modules = {}, catalog, moduleIds }) {
  const active = moduleIds.filter((id) => modules[id]);
  if (!active.length) return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {active.map((id) => (
        <Badge
          key={id}
          variant="outline"
          className="text-[10px] font-normal border-[#0F2D5C]/20 text-[#0F2D5C]"
        >
          {moduleLabel(id, catalog)}
        </Badge>
      ))}
    </div>
  );
}

/**
 * Ficha completa de solicitud — Centro SaaS (diseñador/BNT).
 */
export default function PlatformLicenseRequestDetailBody({
  selected,
  moduleCatalog,
  saasModuleIds,
  statusBadgeClass,
  formatDateShort,
  formatDateTime,
  displayName,
  paymentRejectReason,
  setPaymentRejectReason,
  paymentValidating,
  onPaymentValidate,
  onPaymentReject,
  onOpenFulfill,
  onOpenExpand,
}) {
  if (!selected) return null;

  const isExpansion = isExpansionRequestKind(selected.request_kind);
  const paymentLabel =
    selected.payment_validation_status_label
    || paymentValidationStatusLabel(selected.payment_validation_status);

  return (
    <div className="space-y-3 text-sm" data-testid="platform-license-request-detail">
      <div className="flex flex-wrap gap-2 items-center">
        <span
          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${statusBadgeClass(selected.status)}`}
        >
          {licenseRequestStatusLabel(selected.status)}
        </span>
        <span className="text-xs text-slate-500">ID {selected.id}</span>
      </div>

      {licenseRequestHasCommercialDrift(selected) ? (
        <DetailSection title="Datos comerciales actuales" testId="platform-detail-current-commercial">
          <dl className="grid gap-2 sm:grid-cols-2 m-0">
            <Field label="Nombre / razón social" value={safeDisplayValue(licenseRequestCurrentCommercialName(selected))} />
            <Field label="Código comercial" value={safeDisplayValue(licenseRequestCommercialCodeLabel(selected) || "—")} mono />
            <Field label="RFC (fiscal)" value={safeDisplayValue(licenseRequestCurrentCommercialRfc(selected))} mono />
            <Field label="organization_id" value={safeDisplayValue(selected.organization_id || selected.current_commercial?.organization_id)} mono />
            <Field label="tenant_buyer_id" value={safeDisplayValue(selected.tenant_buyer_id || selected.current_commercial?.tenant_buyer_id)} mono />
            <Field label="Fuente" value={safeDisplayValue(licenseRequestCurrentCommercialSourceLabel(selected))} />
          </dl>
        </DetailSection>
      ) : null}

      <DetailSection title="Solicitud original" testId="platform-detail-original-request">
        <dl className="grid gap-2 sm:grid-cols-2 m-0">
          <Field label="Razón social" value={safeDisplayValue(licenseRequestOriginalDisplayName(selected))} />
          <Field label="Nombre comercial" value={safeDisplayValue(selected.nombre_comercial)} />
          <Field label="RFC" value={safeDisplayValue(licenseRequestOriginalRfc(selected))} mono />
          <Field label="Resumen" value={safeDisplayValue(licenseRequestOriginalSummary(selected))} />
          <Field label="Correo" value={safeDisplayValue(selected.email)} />
          <Field label="Teléfono" value={safeDisplayValue(selected.telefono)} />
          <Field label="Responsable" value={safeDisplayValue(selected.responsable || selected.nombre_comercial)} />
        </dl>
      </DetailSection>

      {!licenseRequestHasCommercialDrift(selected) ? (
      <DetailSection title="Resumen del comprador" testId="platform-detail-buyer">
        <dl className="grid gap-2 sm:grid-cols-2 m-0">
          <Field label="Razón social" value={safeDisplayValue(licenseRequestDisplayName(selected))} />
          <Field label="Nombre comercial" value={safeDisplayValue(selected.nombre_comercial)} />
          <Field label="RFC" value={safeDisplayValue(licenseRequestDisplayRfc(selected))} mono />
          <Field label="Correo" value={safeDisplayValue(selected.email)} />
          <Field label="Teléfono" value={safeDisplayValue(selected.telefono)} />
          <Field label="Responsable" value={safeDisplayValue(selected.responsable || selected.nombre_comercial)} />
        </dl>
      </DetailSection>
      ) : null}

      <DetailSection title="Datos de instalación" testId="platform-detail-installation">
        <dl className="grid gap-2 sm:grid-cols-2 m-0">
          <Field label="ID instalación" value={safeDisplayValue(selected.installation_id)} mono />
          <Field label="Titular (owner_user_id)" value={safeDisplayValue(selected.owner_user_id)} mono />
          <Field label="Organización" value={safeDisplayValue(selected.organization_id, "—")} mono />
          <Field label="Fecha / hora solicitud" value={formatDateTime(selected.created_at || selected.last_submitted_at)} />
          <Field label="Método" value={requestChannelLabel(selected.request_channel)} />
          <Field label="Tipo de flujo" value={requestKindLabel(selected.request_kind)} />
          {selected.expansion_type_label ? (
            <Field label="Tipo ampliación" value={selected.expansion_type_label} />
          ) : null}
        </dl>
      </DetailSection>

      {isExpansion ? (
        <DetailSection title="Licencia actual" testId="platform-detail-current-license">
          <dl className="grid gap-2 sm:grid-cols-2 m-0">
            <Field
              label="Licencia activa (org)"
              value={safeDisplayValue(selected.current_organization_license_id, "—")}
              mono
            />
            <Field label="Revisión actual" value={safeDisplayValue(selected.current_license_revision, "—")} />
            <Field label="Usuarios autorizados" value={safeDisplayValue(selected.current_authorized_users, "—")} />
            <Field label="Clientes autorizados" value={safeDisplayValue(selected.current_authorized_clients, "—")} />
            <Field label="Vigencia desde" value={safeDisplayValue(selected.current_valid_from, "—")} />
            <Field label="Vigencia hasta" value={safeDisplayValue(selected.current_valid_until, "—")} />
          </dl>
          <div className="pt-2">
            <dt className="text-[10px] text-slate-500 uppercase tracking-wide">Módulos actuales</dt>
            <dd className="mt-1 m-0">
              <RequestedModuleBadges
                modules={selected.current_modules}
                catalog={moduleCatalog}
                moduleIds={saasModuleIds}
              />
            </dd>
          </div>
        </DetailSection>
      ) : null}

      {isExpansion ? (
        <DetailSection title="Cambio solicitado" testId="platform-detail-requested-change">
          <Field label="Resumen" value={formatExpansionLimitsSummary(selected)} />
          <dl className="grid gap-2 sm:grid-cols-2 m-0 pt-1">
            <Field label="Usuarios solicitados" value={safeDisplayValue(selected.requested_authorized_users, "—")} />
            <Field label="Clientes solicitados" value={safeDisplayValue(selected.requested_authorized_clients, "—")} />
            <Field label="Vigencia solicitada" value={safeDisplayValue(selected.requested_valid_until, "—")} />
          </dl>
          <div className="pt-2">
            <dt className="text-[10px] text-slate-500 uppercase tracking-wide">Módulos solicitados</dt>
            <dd className="mt-1 m-0">
              <RequestedModuleBadges
                modules={selected.requested_modules}
                catalog={moduleCatalog}
                moduleIds={saasModuleIds}
              />
            </dd>
          </div>
        </DetailSection>
      ) : null}

      <DetailSection title={isExpansion ? "Detalle comercial" : "Módulos y licencia solicitados"} testId="platform-detail-modules">
        <Field
          label="Plan comercial"
          value={
            selected.commercial_plan
              ? `${commercialPlanLabel(selected.commercial_plan)} · ${billingCadenceLabel(selected.billing_cadence)}`
              : "Solicitud legacy — el Diseñador debe asignar Básico o Profesional"
          }
        />
        <Field
          label="Tipo de licencia solicitado"
          value={
            selected.requested_license_type
              ? licenseTypeLabel(selected.requested_license_type)
              : "No especificado"
          }
        />
        {selected.issued_license_type ? (
          <Field label="Tipo emitido" value={licenseTypeLabel(selected.issued_license_type)} />
        ) : null}
        <div className="pt-1">
          <dt className="text-[10px] text-slate-500 uppercase tracking-wide">Módulos solicitados</dt>
          <dd className="mt-1 m-0">
            <RequestedModuleBadges
              modules={selected.requested_modules}
              catalog={moduleCatalog}
              moduleIds={saasModuleIds}
            />
          </dd>
        </div>
        {(selected.commercial_review_required || selected.special_quote_required) && (
          <div className="flex flex-wrap gap-2 pt-1">
            {selected.commercial_review_required ? (
              <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-900 bg-amber-50">
                Requiere revisión comercial
              </Badge>
            ) : null}
            {selected.special_quote_required ? (
              <Badge variant="outline" className="text-[10px] border-violet-300 text-violet-900 bg-violet-50">
                Cotización especial
              </Badge>
            ) : null}
          </div>
        )}
        <Field label="Contabilidad propia" value={selected.wants_own_accounting ? "Sí" : "No"} />
        <Field label="Comentarios del comprador" value={safeDisplayValue(selected.comments)} />
        <Field label="Notas de revisión BNT" value={safeDisplayValue(selected.review_notes)} />
      </DetailSection>

      <DetailSection title="Alcance estimado" testId="platform-detail-scope">
        <Field label="Resumen" value={formatScopeSummary(selected)} />
        <dl className="grid gap-2 sm:grid-cols-2 m-0 pt-1">
          <Field label="Usuarios estimados" value={safeDisplayValue(selected.estimated_users, "—")} />
          <Field label="Clientes / empresas estimadas" value={safeDisplayValue(selected.estimated_clients, "—")} />
        </dl>
        {(selected.authorized_users || selected.authorized_clients) ? (
          <dl className="grid gap-2 sm:grid-cols-2 m-0 pt-1 border-t border-slate-200/80">
            <Field label="Usuarios autorizados (emisión)" value={safeDisplayValue(selected.authorized_users, "—")} />
            <Field label="Clientes autorizados (emisión)" value={safeDisplayValue(selected.authorized_clients, "—")} />
          </dl>
        ) : null}
      </DetailSection>

      <DetailSection title="Datos de facturación" testId="platform-detail-invoice">
        <Field label="Requiere factura" value={formatRequiresInvoiceLabel(selected.requires_invoice)} />
        {selected.requires_invoice ? (
          <dl className="grid gap-2 m-0 pt-1">
            <Field label="RFC factura" value={safeDisplayValue(selected.invoice_rfc)} mono />
            <Field label="Razón social" value={safeDisplayValue(selected.invoice_razon_social)} />
            <Field label="Régimen fiscal" value={safeDisplayValue(selected.invoice_regimen_fiscal)} />
            <Field label="Código postal" value={safeDisplayValue(selected.invoice_codigo_postal)} />
            <Field label="Domicilio fiscal" value={safeDisplayValue(selected.invoice_fiscal_address)} />
            <Field label="Correo facturación" value={safeDisplayValue(selected.invoice_email)} />
            <Field
              label="Uso de CFDI"
              value={selected.invoice_cfdi_use_label || cfdiUseSelectLabel(selected.invoice_cfdi_use) || "No especificado"}
            />
            <Field
              label="Forma de pago"
              value={
                selected.invoice_payment_method_label
                || paymentMethodSelectLabel(selected.invoice_payment_method)
                || "No especificado"
              }
            />
            <Field label="Estatus facturación" value={invoiceStatusLabel(selected.invoice_status)} />
            <Field label="Comentarios factura" value={safeDisplayValue(selected.invoice_comments)} />
          </dl>
        ) : null}
      </DetailSection>

      <DetailSection title="Comprobante de pago" testId="platform-detail-payment">
        <Field label="Estado" value={paymentLabel} />
        {selected.payment_proof_filename ? (
          <Field
            label="Archivo"
            value={`${selected.payment_proof_filename}${selected.payment_proof_uploaded_at ? ` · ${formatDateTime(selected.payment_proof_uploaded_at)}` : ""}`}
          />
        ) : null}
        {selected.payment_comment ? (
          <Field label="Comentario del comprador" value={selected.payment_comment} />
        ) : null}
        {selected.payment_validated_at ? (
          <Field
            label="Validación"
            value={`${formatDateTime(selected.payment_validated_at)}${selected.payment_validated_by ? ` · ${selected.payment_validated_by}` : ""}`}
          />
        ) : null}
        {selected.payment_rejection_reason ? (
          <Field label="Motivo de rechazo" value={selected.payment_rejection_reason} />
        ) : null}
        {selected.payment_proof_file_id ? (
          <Button asChild size="sm" variant="outline" className="h-8 text-xs mt-1">
            <a
              href={platformPaymentProofDownloadUrl(selected.id)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="platform-payment-proof-download"
            >
              Ver / descargar comprobante
            </a>
          </Button>
        ) : null}
        {isPaymentProofPending(selected) ? (
          <div className="space-y-2 pt-2 border-t border-slate-200/80">
            <Button
              type="button"
              size="sm"
              className="h-8 text-xs bg-emerald-700 hover:bg-emerald-800"
              disabled={paymentValidating}
              onClick={onPaymentValidate}
              data-testid="platform-payment-validate-btn"
            >
              Marcar pago validado
            </Button>
            <div>
              <Label htmlFor="payment-reject-reason" className="text-xs">
                Motivo de rechazo
              </Label>
              <SpellcheckTextarea
                id="payment-reject-reason"
                value={paymentRejectReason}
                onChange={(e) => setPaymentRejectReason(e.target.value)}
                rows={2}
                className="mt-1 text-xs"
                placeholder="Indique por qué no corresponde el comprobante"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 text-xs mt-2 border-red-200 text-red-800"
                disabled={paymentValidating}
                onClick={onPaymentReject}
                data-testid="platform-payment-reject-btn"
              >
                Rechazar comprobante
              </Button>
            </div>
          </div>
        ) : null}
      </DetailSection>

      <DetailSection title="Seguimiento y legal" testId="platform-detail-tracking">
        <dl className="grid gap-2 sm:grid-cols-2 m-0">
          <Field label="Avisos / recordatorios" value={String(selected.reminder_count || 0)} />
          <Field
            label="Último aviso"
            value={selected.reminder_requested_at ? formatDateTime(selected.reminder_requested_at) : "—"}
          />
          <Field label="Revisado el" value={formatDateTime(selected.reviewed_at)} />
          <Field label="Revisado por" value={safeDisplayValue(selected.reviewed_by_user_id, "—")} mono />
        </dl>
        {selected.agreement_acceptance_id ? (
          <>
            <Field
              label="Aceptación legal vinculada"
              value={safeDisplayValue(selected.agreement_acceptance_id)}
              mono
            />
            {selected.agreement_reused ? (
              <Field label="Aceptación legal" value="Reutilizada de solicitud/licencia previa" />
            ) : null}
            <Field
              label="Versión legal"
              value={safeDisplayValue(selected.contract_version, "—")}
            />
            <Field
              label="Fecha de aceptación"
              value={selected.accepted_at ? formatDateTime(selected.accepted_at) : "—"}
            />
            <a
              href={platformAgreementAcceptancePdfUrl(selected.agreement_acceptance_id)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-xs font-medium text-[#0F2D5C] hover:underline mt-2"
            >
              Descargar constancia legal
            </a>
          </>
        ) : null}
        {(selected.request_history || []).length > 0 ? (
          <ul className="space-y-1 text-xs text-slate-600 max-h-40 overflow-y-auto m-0 pl-0 list-none pt-2 border-t border-slate-200/80">
            {(selected.request_history || []).slice().reverse().map((ev, idx) => (
              <li key={`${ev.at}-${idx}`} className="border-b border-slate-50 pb-1">
                <span className="text-slate-400">{formatDateTime(ev.at)}</span>
                {" · "}
                <span className="font-medium">{requestHistoryEventLabel(ev.event)}</span>
                {ev.note ? `: ${ev.note}` : ""}
              </li>
            ))}
          </ul>
        ) : null}
      </DetailSection>

      {(selected.linked_organization_license_id || selected.linked_license_registry_id) ? (
        <DetailSection title="Vínculos de emisión" testId="platform-detail-links">
          {selected.linked_organization_license_id ? (
            <Field label="Licencia organización" value={selected.linked_organization_license_id} mono />
          ) : null}
          {selected.linked_license_registry_id ? (
            <Field label="Registro distribuidor" value={selected.linked_license_registry_id} mono />
          ) : null}
        </DetailSection>
      ) : null}

      {selected.status === "approved" && isPaymentProofPending(selected) && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {PLATFORM_LICENSE_REQUEST_MSG_PAYMENT_PENDING} Valide el comprobante antes de emitir la licencia.
        </div>
      )}
      {selected.status === "approved" && canFulfillPlatformLicenseRequest(selected) && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 space-y-2">
          <p className="m-0">{PLATFORM_LICENSE_REQUEST_MSG_APPROVED_NEXT}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              className="bg-[#0F2D5C] hover:bg-[#0a2247]"
              onClick={onOpenFulfill}
            >
              <Sparkles className="w-3.5 h-3.5 mr-1" />
              Emitir licencia web
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link to={PLATFORM_LICENSE_REQUEST_EMIT_PATH}>Emisión manual avanzada</Link>
            </Button>
          </div>
        </div>
      )}
      {selected.status === "issued" && (
        <div className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900 space-y-2">
          <p className="m-0">
            Licencia emitida. El comprador debe refrescar sesión para ver módulos autorizados.
          </p>
          {canExpandIssuedPlatformLicenseRequest(selected) && onOpenExpand ? (
            <Button
              type="button"
              size="sm"
              className="bg-[#0F2D5C] hover:bg-[#0a2247]"
              onClick={onOpenExpand}
              data-testid="detail-expand-issued-license"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Ampliar / renovar licencia
            </Button>
          ) : null}
        </div>
      )}

      <div className="border-t pt-3">
        <p className="text-xs text-slate-500 mb-2">Acciones avanzadas</p>
        <Button asChild size="sm" variant="outline">
          <Link
            to={commercialToolsLinkQuery({
              email: selected.email,
              rfc: selected.rfc,
              owner_user_id: selected.owner_user_id,
            })}
          >
            Diagnóstico / reinicio comercial
          </Link>
        </Button>
      </div>
    </div>
  );
}
